import { useNavigate } from 'react-router-dom';
import { PhoneIncoming, User, UserPlus, FileText, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCallerLookup } from '@/hooks/useCallerLookup';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import type { CtmActivityDetail } from './SoftphoneProvider';

interface IncomingCallPopProps {
  activity: CtmActivityDetail | null;
  onOpenPhone: () => void;
}

// Non-modal card pinned top-right: informs without stealing keyboard focus
// while a CSR may be mid-typing. Rendered only while the phone is ringing.
export function IncomingCallPop({ activity, onOpenPhone }: IncomingCallPopProps) {
  const navigate = useNavigate();
  const callerNumber =
    activity?.caller_number || activity?.contact_number || activity?.caller_number_format || '';
  const callerName = activity?.name || activity?.cnam || '';

  const { data: lookup, isLoading } = useCallerLookup(callerNumber);
  const { formatShortDate } = useCompanyLocale();
  const customer = lookup?.customer ?? null;
  const lead = lookup?.lead ?? null;

  return (
    <Card className="fixed top-4 right-4 z-[60] w-80 shadow-xl border-success/50 animate-in slide-in-from-top-2">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <PhoneIncoming className="h-4 w-4 text-success animate-pulse" />
          Incoming call
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="font-medium">{callerName || callerNumber || 'Unknown caller'}</p>
          {callerName && callerNumber && (
            <p className="text-sm text-muted-foreground">{callerNumber}</p>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Looking up caller…</p>
        ) : customer ? (
          <div className="rounded-md bg-muted p-2 text-sm space-y-1">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5" />
              <span className="font-medium">
                Past customer:{' '}
                <CustomerLink customerId={customer.id} name={customer.customer_name} />
              </span>
            </div>
            <p className="text-muted-foreground">
              {customer.jobs_count} {customer.jobs_count === 1 ? 'job' : 'jobs'}
              {customer.last_job?.job_date && ` · last ${formatShortDate(customer.last_job.job_date)}`}
              {customer.last_job?.status && ` (${customer.last_job.status})`}
            </p>
          </div>
        ) : lead ? (
          <div className="rounded-md bg-muted p-2 text-sm space-y-1">
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5" />
              <span className="font-medium">Open lead: {lead.customer_name}</span>
              <Badge variant="outline" className="text-xs">{lead.status}</Badge>
            </div>
            {lead.assigned_csr_name && (
              <p className="text-muted-foreground">Assigned to {lead.assigned_csr_name}</p>
            )}
          </div>
        ) : (
          <div className="rounded-md bg-muted p-2 text-sm">
            <span className="font-medium">New caller</span> — no matching customer or lead
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {customer && (
            <Button size="sm" variant="secondary" onClick={() => navigate(`/customers/${customer.id}`)}>
              <User className="h-3.5 w-3.5 mr-1" /> Open customer
            </Button>
          )}
          {lead && (
            <Button size="sm" variant="secondary" onClick={() => navigate(`/leads/${lead.id}`)}>
              <FileText className="h-3.5 w-3.5 mr-1" /> Open lead
            </Button>
          )}
          {!customer && !lead && callerNumber && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                navigate('/leads/new', {
                  state: { prefill: { customer_phone: callerNumber, customer_name: callerName } },
                })
              }
            >
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Create lead
            </Button>
          )}
          <Button size="sm" onClick={onOpenPhone}>
            <Phone className="h-3.5 w-3.5 mr-1" /> Open phone
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
